import moment from 'moment-timezone';
import angularModule from './authentication_module';
import Capabilities from 'Capabilities';
import { getNativeProviderData, NATIVE_LOGIN_CANCELLED_CODE } from '../../nativeSocialLogin';

angularModule.factory('AuthFormHelperMixin', [
    '$injector',

    function factory($injector) {
        const $q = $injector.get('$q');
        const $rootScope = $injector.get('$rootScope');
        const safeApply = $injector.get('safeApply');
        const AuthClient = $injector.get('AuthClient');
        const $window = $injector.get('$window');
        const omniauth = $injector.get('omniauth');
        const ConfigFactory = $injector.get('ConfigFactory');
        const reactRouterBridge = $injector.get('reactRouterBridge');
        const TranslationHelper = $injector.get('TranslationHelper');
        const ErrorLogService = $injector.get('ErrorLogService');
        const ClientStorage = $injector.get('ClientStorage');
        const EventLogger = $injector.get('EventLogger');
        const HttpQueue = $injector.get('HttpQueue');

        function safeStringify(value) {
            try {
                return JSON.stringify(value);
            } catch {
                return `Unserializable value: ${typeof value}`;
            }
        }

        // 403 = server-side account-state rejections (deactivated, in deletion queue).
        const isIgnorableError = error => error?.status === 403 || error?.code === NATIVE_LOGIN_CANCELLED_CODE;

        const translationHelper = new TranslationHelper('authentication.auth_form_helper_mixin');

        return {
            onLink(scope, onOmniauthSuccess) {
                scope.preventSubmit = false;

                scope.form_errors = {};

                if (!Capabilities.canSignIn) {
                    scope.form_errors.general = translationHelper.get('cookies_required');
                    scope.preventSubmit = true;
                }

                // Auth::OmniauthCallbacksController#redirect_failure passes back an already-localized error_description.
                if (reactRouterBridge.search().error_description) {
                    scope.form_errors.general = reactRouterBridge.search().error_description;
                }

                // needed on scope for tests
                scope.getNativeOauthPluginPromise = (provider, signUpCode) => {
                    const baseParams = {
                        sign_up_code: signUpCode,
                        timezone: moment.tz.guess(),
                    };

                    // The plugin configuration maps to a SHA-1 from the production certificates, so the webClientId must also be the
                    // production one. Set it in application.env to test native Google Sign-In.
                    const googleWebClientId = ConfigFactory.getSync().google_oauth_id;

                    return $q
                        .when(getNativeProviderData(provider, googleWebClientId))
                        .then(providerData => ({ ...baseParams, provider_data: providerData }));
                };

                scope.loginWithProvider = (provider, signUpCode, programType) => {
                    // We know that it is possible for the signUpCode to not be set yet in
                    // CORDOVA, but that shouldn't be a problem as we default the signUpCode on
                    // the server-side if it doesn't exist. See - https://trello.com/c/JDHMbJQr
                    if (!signUpCode && !$window.CORDOVA) {
                        ErrorLogService.notify('loginWithProvider called with no signUpCode');
                    }

                    scope.preventSubmit = true;

                    // Since logging in with oauth will send us away from the page,
                    // try to make extra sure these events get logged.
                    EventLogger.log('auth:login_with_provider', { provider });

                    // Note that we need to wait for the request to finish before starting the OAuth
                    // flow because of concurrency issues with the session state (which is actually
                    // only needed for OAuth). Theoretically it's not possible right now to completely
                    // eliminate concurrency issues here because auth requests are exempt from the HttpQueue,
                    // and the status ping could theoretically happen at just the right time, and tinyEventLogger
                    // requests are outside Angular and thus not using the HttpQueue. But practically this
                    // particular events request is the main offender since it's happening at the same time we
                    // initiate OAuth.
                    // See https://trello.com/c/905quJK9
                    // BUT, if the queue is blocked by a failed request (and so we're showing the login modal), then
                    // we can't flush the events (and we may want to, since we may have failed to save something else)
                    // see https://trello.com/c/6SKC69NI
                    const promise = HttpQueue.blockedByFailedRequest ? $q.resolve() : EventLogger.tryToSaveBuffer();

                    // Initiate Omniauth on web
                    if (!window.CORDOVA) {
                        return promise.then(() => omniauth.loginWithProvider(provider, signUpCode, programType));
                    }

                    // Initiate native OAuth plugin on mobile
                    return promise
                        .then(() => scope.getNativeOauthPluginPromise(provider, signUpCode))
                        .then(params => AuthClient.exchangeNativeOauth(provider, params))
                        .then(user => {
                            $rootScope.$broadcast('auth:login-success', user);
                            safeApply($rootScope);

                            scope.preventSubmit = false;
                            scope.form_errors = {};

                            // Wait for ValidationResponder to tell us we've authenticated, to ensure
                            // we have a currentUser before running the success callback
                            const stopListeningForLoginSuccess = $rootScope.$on(
                                'validation-responder:login-success',
                                () => {
                                    if (onOmniauthSuccess) {
                                        onOmniauthSuccess.call(this);
                                    }
                                    stopListeningForLoginSuccess();
                                },
                            );
                            scope.$on('$destroy', () => {
                                stopListeningForLoginSuccess();
                            });
                        })
                        .catch(error => {
                            scope.preventSubmit = false;

                            // clear any temporary redirect targets
                            ClientStorage.removeItem('oauth_redirect_target');

                            // Auth::NativeOauthExchangesController#exchange renders an already-localized error_description on failure.
                            // Fall back to a generic message on transport errors that arrive without a response body,
                            // but skip the fallback for user-cancellations (they shouldn't surface as form errors).
                            if (error.data?.error_description) {
                                scope.form_errors.general = error.data.error_description;
                            } else if (!isIgnorableError(error)) {
                                scope.form_errors.general = translationHelper.get('server_error');
                            }

                            safeApply($rootScope);

                            if (!isIgnorableError(error)) {
                                ErrorLogService.notify('Failed to login (native)', undefined, {
                                    parsedError: safeStringify(error),
                                    provider,
                                });
                            }
                        });
                };

                scope.formErrorClasses = fieldName => {
                    if (scope.form_errors[fieldName]) {
                        return ['form-error', 'active'];
                    }
                    return ['form-error'];
                };

                scope.formValidationClasses = fieldName => {
                    if (scope.form_errors[fieldName]) {
                        return ['form-control', 'ng-invalid'];
                    }
                    return ['form-control'];
                };

                scope.uniqueErrors = errors => {
                    if (Array.isArray(errors)) {
                        errors = [...new Set(errors)].join('\n');
                    }
                    return errors;
                };

                scope.submitLogin = signInInfo => {
                    scope.preventSubmit = true;
                    scope.form_errors = {};

                    if (!signInInfo.provider) {
                        signInInfo.provider = scope.urlConfig.provider;
                    }

                    return (
                        AuthClient.signIn(signInInfo)
                            // using then/catch here rather than .finally to prevent
                            // rejected promises from bubbling up to the caller
                            .then(() => {
                                scope.preventSubmit = false;
                                safeApply($rootScope);
                            })
                            .catch(() => {
                                scope.preventSubmit = false;
                                safeApply($rootScope);
                            })
                    );
                };

                // FrontRoyalApiErrorHandler ignores /auth/* URLs, so we surface the sign-in error here.
                // Auth::SessionsController#create renders an already-localized `error_description` for every failure mode;
                // we fall back to a generic message on transport errors that arrive without a response body.
                const errorListener = $rootScope.$on('auth:login-error', (_event, response) => {
                    scope.form_errors.general =
                        response?.data?.error_description || translationHelper.get('server_error');
                });

                scope.$on('$destroy', () => {
                    if (errorListener) {
                        errorListener();
                    }
                });
            },
        };
    },
]);
